import { addDaysBRTFrom, getTodayBRT } from './dateUtils';

export interface MedLike {
  start_date?: string | null;
  end_date?: string | null;
  frequency?: string | null;
  usage_days?: number | null;
  pause_days?: number | null;
  total_period_months?: number | null;
  times?: string[] | null;
  is_active?: boolean | null;
}

/**
 * Verifica se a medicação deve ser considerada nas contagens de HOJE (BRT)
 * Regra unificada:
 * - Exclui se hoje < start_date
 * - Exclui se finalizada hoje (>= end_date/planned_end)
 * - Exclui se em pausa hoje
 */
export function isMedicationEffectiveToday(med: MedLike, ymd?: string): boolean {
  const today = ymd || getTodayBRT();
  const start = med.start_date || null;
  if (start && today < start) return false;
  if (isMedicationFinishedByYmd(med, today)) return false;
  if (isMedicationInPauseOn(med, today)) return false;
  return true;
}

function parseLocalDate(ymd?: string | null): Date | null {
  if (!ymd) return null;
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, (m || 1) - 1, d || 1);
}

export function getPlannedEndYmd(med: MedLike): string | null {
  const months = Number(med.total_period_months) || 0;
  const start = med.start_date || null;
  if (!start || months <= 0) return null;
  const days = months * 30 - 1;
  return addDaysBRTFrom(start, days);
}

export function isMedicationFinishedByYmd(med: MedLike, ymd: string): boolean {
  // Regra: finalizar APÓS o último dia do tratamento (> end_date/planned_end)
  // No último dia do tratamento (dia 30/30): respeita o ciclo de pausa (pode estar ativa OU em pausa)
  // Após o último dia (dia 31+): sempre finalizada, independente de ciclo
  const end = med.end_date || null;
  if (end) {
    if (ymd > end) return true;
  }
  const planned = getPlannedEndYmd(med);
  if (planned) {
    if (ymd > planned) return true;
  }
  return false;
}

export function isMedicationInPauseOn(med: MedLike, ymd?: string): boolean {
  const today = ymd || getTodayBRT();
  const refDate = parseLocalDate(today);
  if (!refDate) return false;

  const startDate = parseLocalDate(med.start_date || undefined);
  if (!startDate) return false;
  if (refDate < startDate) return false;

  // Fora do período do tratamento
  if (isMedicationFinishedByYmd(med, today)) return false;

  // End date (exclusão já tratada acima via finished)

  // Lógica especial para modo custom (dias específicos da semana)
  const frequency = (med.frequency || '').toString();
  if (frequency.startsWith('custom;')) {
    const parts = frequency.split(';');
    if (parts[1]) {
      try {
        const customDayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
        const dayOfWeek = refDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
        // Está em pausa se o dia da semana NÃO está na lista
        return !customDayIndices.includes(dayOfWeek);
      } catch {
        // se falhar o parse, continua para lógica padrão
      }
    }
  }

  // Lógica padrão para outros modos (daily, alternate, weekly)
  const usageDays = Number(med.usage_days) || 0;
  const pauseDays = Number(med.pause_days) || 0;
  if (usageDays <= 0 || pauseDays <= 0) return false;

  const cycleLength = usageDays + pauseDays;
  if (cycleLength <= 0) return false;

  const msPerDay = 24 * 60 * 60 * 1000;
  const daysElapsed = Math.floor((refDate.getTime() - startDate.getTime()) / msPerDay);
  const position = ((daysElapsed % cycleLength) + cycleLength) % cycleLength;
  return position >= usageDays;
}
